import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Launch as LaunchIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material';
import { formatDateSafe } from '../../../shared/utils/dateHelpers';
import { LEGACY_PLANNED_COMMENT, latestCountedRecord } from '../../../shared/success/successRecords';
import { ClientSuccessHistoryEntry } from '../nomination.types';
import { fetchClientSuccessHistory } from '../services/nominationService';
import { normalizeClientName } from '../utils/nominationFilters';

export interface ClientSuccessHistoryDialogProps {
  open: boolean;
  clientId: string | null;
  clientName: string;
  onClose: () => void;
  onViewClient: (clientId: string) => void;
}

/**
 * Shows a client's previous `client_success` entries (goal + targets + comments)
 * so a reviewer can judge a nomination without leaving the nominations page.
 * Results are cached per client for the lifetime of the page.
 */
export const ClientSuccessHistoryDialog: React.FC<ClientSuccessHistoryDialogProps> = ({
  open,
  clientId,
  clientName,
  onClose,
  onViewClient,
}) => {
  const [entries, setEntries] = useState<ClientSuccessHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const cacheRef = useRef<Map<string, ClientSuccessHistoryEntry[]>>(new Map());

  useEffect(() => {
    if (!open || !clientId) return undefined;

    const cached = cacheRef.current.get(clientId);
    if (cached && attempt === 0) {
      setEntries(cached);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchClientSuccessHistory(clientId)
      .then(result => {
        if (cancelled) return;
        cacheRef.current.set(clientId, result);
        setEntries(result);
      })
      .catch(fetchError => {
        if (cancelled) return;
        console.error('[success-nominations] failed to load client success history', fetchError);
        setError('Unable to load the client success history right now.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, clientId, attempt]);

  useEffect(() => {
    if (!open) setAttempt(0);
  }, [open]);

  const displayName = normalizeClientName(clientName) || clientName;
  const updateCount = entries.filter(entry => entry.kind === 'update').length;
  const latestUpdateId = latestCountedRecord(entries)?.id;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <HistoryIcon color="primary" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" component="span" display="block" noWrap>
              Client success history
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {displayName}
              {updateCount > 0 ? ` · ${updateCount} ${updateCount === 1 ? 'update' : 'updates'}` : ''}
            </Typography>
          </Box>
          <IconButton aria-label="Close" size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: 'background.default' }}>
        {isLoading && <LinearProgress sx={{ mb: 2 }} />}

        {error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => setAttempt(current => current + 1)}>
                Retry
              </Button>
            }
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}

        {!isLoading && !error && entries.length === 0 && (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="body1" color="text.secondary">
              No success metrics have been recorded for this client yet.
            </Typography>
          </Paper>
        )}

        <Stack spacing={2}>
          {entries.map(entry => (
            <Paper
              key={entry.id}
              data-entry-id={entry.id}
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 2,
                borderColor: entry.goal_achieved ? 'success.light' : 'divider',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="caption" color="text.secondary">
                      {entry.id === latestUpdateId ? 'Latest · ' : ''}
                      Recorded {formatDateSafe(entry.created_at, 'd MMM yyyy', 'Unknown date')}
                      {entry.created_by ? ` by ${entry.created_by}` : ''}
                    </Typography>
                    {entry.kind !== 'update' && <Chip label="No update recorded" size="small" variant="outlined" />}
                  </Stack>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 0.5 }}>
                    {entry.goal || 'Goal not set'}
                  </Typography>
                  {entry.goal_update && (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {entry.goal_update}
                    </Typography>
                  )}
                </Box>
                <Chip
                  icon={entry.goal_achieved ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                  label={entry.goal_achieved ? 'Goal achieved' : 'Goal in progress'}
                  color={entry.goal_achieved ? 'success' : 'default'}
                  variant={entry.goal_achieved ? 'filled' : 'outlined'}
                  size="small"
                  sx={{ flexShrink: 0 }}
                />
              </Stack>

              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                {entry.targets.map(target => (
                  <Grid item xs={12} md={4} key={`${entry.id}-${target.label}`}>
                    <Box
                      sx={{
                        p: 1.5,
                        height: '100%',
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: target.achieved ? 'success.light' : 'divider',
                        backgroundColor: target.achieved ? 'rgba(16, 185, 129, 0.06)' : 'transparent',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          {target.label}
                        </Typography>
                        {target.achieved ? (
                          <CheckCircleIcon sx={{ fontSize: 16 }} color="success" />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} color="disabled" />
                        )}
                      </Stack>
                      <Typography variant="body2" fontWeight={500}>
                        {target.value || 'Not set'}
                      </Typography>
                      {target.update && (
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                          {target.update}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {entry.comments && entry.comments !== LEGACY_PLANNED_COMMENT && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, whiteSpace: 'pre-wrap' }}>
                  {entry.comments}
                </Typography>
              )}
            </Paper>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        {clientId && (
          <Button variant="contained" endIcon={<LaunchIcon />} onClick={() => onViewClient(clientId)}>
            Open client success
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ClientSuccessHistoryDialog;
